import { useIntl } from "@leagueplatform/locales";
import type { UseFormReturn } from "@leagueplatform/web-common";
import { FormProvider } from "@leagueplatform/web-common";
import {
  Modal,
  Button,
  StackLayout,
  HeadingText,
  ParagraphText,
  ImageMessage,
} from "@leagueplatform/genesis-core";
import allowTextNotifications from "assets/allow-text-notifications.svg";
import { RHFTextInput } from "components/form-elements/rhf-text-input.component";
import { phoneNumberValidation } from "common/utils/rhf-form-validation.util";
import { useIsBelowTablet } from "hooks/use-is-below-tablet.hook";
import type { CommunicationPreferencesValues } from "components/forms/communications-section/communications-section.component";
import { ModalActionButton } from "../common/modal-action-button.component";

type EnableTextNotificationsModalProps = {
  isOpen: boolean;
  formMethods: UseFormReturn<CommunicationPreferencesValues>;
  onSubmit: (mobilePhoneNumber: string) => Promise<void>;
  onCancel: () => void;
  existingMobileNumber?: string;
  isLoading?: boolean;
};

export const EnableTextNotificationsModal = ({
  isOpen,
  formMethods,
  onSubmit,
  onCancel,
  existingMobileNumber,
  isLoading = false,
}: EnableTextNotificationsModalProps) => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useIsBelowTablet();

  const handleClose = () => {
    formMethods.setValue("mobilePhoneNumber", existingMobileNumber ?? "");
    onCancel();
  };

  const handleValidationError = (errors: Record<string, unknown>) => {
    const firstField = Object.keys(
      errors
    )[0] as keyof CommunicationPreferencesValues;
    if (firstField) formMethods.setFocus(firstField);
  };

  const handleSubmitClick = async () => {
    const isValid = await formMethods.trigger("mobilePhoneNumber");
    if (isValid) {
      const mobilePhoneNumber = formMethods.getValues("mobilePhoneNumber");
      await onSubmit(mobilePhoneNumber ?? "");
    } else {
      handleValidationError(
        formMethods.formState.errors as Record<string, unknown>
      );
    }
  };

  return (
    <Modal.Root open={isOpen}>
      <Modal.Content
        showCloseButton={false}
        padding="$none"
        width={{
          "@initial": 588,
          "@mobile": "100%",
          "@mobileLandscape": "100%",
        }}
      >
        <StackLayout spacing="$none" horizontalAlignment="stretch">
          <StackLayout
            horizontalAlignment="end"
            css={{
              padding: "$half",
              height: "auto",
            }}
          >
            <Button
              priority="tertiary"
              icon="tinyClose"
              size="small"
              hideLabel
              quiet
              onClick={handleClose}
            >
              {formatMessage({ id: "STR_CLOSE" })}
            </Button>
          </StackLayout>
          <StackLayout
            css={{
              padding: "$two",
              paddingBlockStart: "$none",
            }}
            horizontalAlignment="stretch"
            spacing="$one"
          >
            <ImageMessage
              image={allowTextNotifications}
              css={{
                textAlign: "left",
                "@mobile": { padding: "$half" },
              }}
            >
              <Modal.Title>
                <HeadingText level="3" size="xl" css={{ paddingBlock: "$one" }}>
                  {formatMessage({ id: "STR_ENABLE_TEXT_NOTIFICATIONS" })}
                </HeadingText>
              </Modal.Title>
              <Modal.Description>
                <ParagraphText css={{ color: "$onSurfaceTextSubdued" }}>
                  {formatMessage({
                    id: "STR_ENABLE_TEXT_NOTIFICATIONS_BODY",
                  })}
                </ParagraphText>
              </Modal.Description>
            </ImageMessage>

            <FormProvider
              formMethods={formMethods}
              onError={handleValidationError}
            >
              <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
                <RHFTextInput
                  id="mobilePhoneNumber"
                  type="tel"
                  label={formatMessage({ id: "STR_MOBILE_PHONE_NUMBER" })}
                  required
                  requiredMessage={formatMessage({
                    id: "STR_PHONE_VALIDATION_ERROR",
                  })}
                  registerOptions={phoneNumberValidation(
                    formatMessage({ id: "STR_PHONE_VALIDATION_ERROR" })
                  )}
                  css={{
                    width: "60%",
                    "@mobile": { width: "100%" },
                  }}
                  disabled={isLoading}
                />

                <StackLayout
                  horizontalAlignment={{
                    "@initial": "start",
                    "@mobile": "center",
                  }}
                  orientation={{
                    "@initial": "horizontal",
                    "@mobile": "vertical",
                  }}
                  spacing="$one"
                  css={{
                    flexGrow: 0,
                    "@mobile": {
                      flexFlow: "column",
                    },
                  }}
                >
                  <ModalActionButton
                    actionType="submit"
                    onClick={handleSubmitClick}
                    width={isBelowTablet ? "fillContainer" : "hugContents"}
                    buttonText={formatMessage({
                      id: "STR_ALLOW_TEXT_NOTIFICATIONS",
                    })}
                    size="large"
                    priority="primary"
                    loading={isLoading}
                    disabled={isLoading}
                  />
                  <ModalActionButton
                    actionType="cancel"
                    onClick={handleClose}
                    width={isBelowTablet ? "fillContainer" : "hugContents"}
                    buttonText={formatMessage({ id: "STR_NOT_NOW" })}
                    size="large"
                    quiet
                    css={{ color: "$onSurfaceTextSubdued" }}
                    disabled={isLoading}
                  />
                </StackLayout>
              </StackLayout>
            </FormProvider>
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
